const MAX_EXCEL_COLUMN = 16_384;
const MAX_EXCEL_ROW = 1_048_576;

const columnLettersToNumber = (letters: string): number =>
  letters
    .toUpperCase()
    .split("")
    .reduce((column, letter) => column * 26 + letter.charCodeAt(0) - 64, 0);

const columnNumberToLetters = (column: number): string => {
  let remaining = column;
  let letters = "";

  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    remaining = Math.floor((remaining - 1) / 26);
  }

  return letters;
};

// Match A1 references without treating function names (LOG10(...)), sheet
// names (Sheet1!A1), identifiers, or invalid Excel coordinates as cells. The
// leading boundary is captured so it can be preserved by String.replace.
const CELL_REFERENCE =
  /(^|[^A-Za-z0-9_.])(\$?)([A-Za-z]{1,3})(\$?)([1-9]\d*)(?![A-Za-z0-9_.(!]|'!)/g;

const translateUnquotedReferences = (
  text: string,
  rowOffset: number,
  columnOffset: number,
): string =>
  text.replace(
    CELL_REFERENCE,
    (
      match,
      boundary: string,
      absoluteColumn: string,
      columnLetters: string,
      absoluteRow: string,
      rowDigits: string,
    ) => {
      const originalColumn = columnLettersToNumber(columnLetters);
      const originalRow = Number(rowDigits);

      if (originalColumn > MAX_EXCEL_COLUMN || originalRow > MAX_EXCEL_ROW) {
        return match;
      }

      const column = absoluteColumn
        ? originalColumn
        : originalColumn + columnOffset;
      const row = absoluteRow ? originalRow : originalRow + rowOffset;

      if (
        column < 1 ||
        column > MAX_EXCEL_COLUMN ||
        row < 1 ||
        row > MAX_EXCEL_ROW
      ) {
        return `${boundary}#REF!`;
      }

      return `${boundary}${absoluteColumn}${columnNumberToLetters(column)}${absoluteRow}${row}`;
    },
  );

const convertUnquotedReferencesToR1C1 = (
  text: string,
  sourceRow: number,
  sourceCol: number,
): string =>
  text.replace(
    CELL_REFERENCE,
    (
      match,
      boundary: string,
      absoluteColumn: string,
      columnLetters: string,
      absoluteRow: string,
      rowDigits: string,
    ) => {
      const column = columnLettersToNumber(columnLetters);
      const row = Number(rowDigits);
      if (column > MAX_EXCEL_COLUMN || row > MAX_EXCEL_ROW) return match;

      const rowReference = absoluteRow
        ? `R${row}`
        : row === sourceRow
          ? "R"
          : `R[${row - sourceRow}]`;
      const columnReference = absoluteColumn
        ? `C${column}`
        : column === sourceCol
          ? "C"
          : `C[${column - sourceCol}]`;

      return `${boundary}${rowReference}${columnReference}`;
    },
  );

const transformUnquotedFormulaSegments = (
  formula: string,
  transform: (segment: string) => string,
) => {
  let transformed = "";
  let unquotedStart = 0;

  for (let index = 0; index < formula.length; index += 1) {
    const quote = formula[index];
    if (quote !== '"' && quote !== "'") continue;

    transformed += transform(formula.slice(unquotedStart, index));

    const quotedStart = index;
    index += 1;
    while (index < formula.length) {
      if (formula[index] !== quote) {
        index += 1;
        continue;
      }
      if (formula[index + 1] === quote) {
        index += 2;
        continue;
      }
      break;
    }

    transformed += formula.slice(
      quotedStart,
      Math.min(index + 1, formula.length),
    );
    unquotedStart = Math.min(index + 1, formula.length);
  }

  return transformed + transform(formula.slice(unquotedStart));
};

// Apply an Excel-style copy offset to A1 references. Double-quoted formula
// strings and single-quoted sheet names are copied byte-for-byte, including
// Excel's doubled-quote escapes, so cell-looking text inside them is inert.
export const translateFormulaReferences = (
  formula: string,
  rowOffset: number,
  columnOffset: number,
): string => {
  if (!formula.trimStart().startsWith("=")) return formula;
  if (rowOffset === 0 && columnOffset === 0) return formula;

  return transformUnquotedFormulaSegments(formula, (segment) =>
    translateUnquotedReferences(segment, rowOffset, columnOffset),
  );
};

// Google Sheets' clipboard formula metadata uses relative R1C1 notation.
// Converting against each copied cell preserves the same relative/absolute
// semantics when Sheets pastes the range at any destination.
export const convertFormulaToR1C1 = (
  formula: string,
  sourceRow: number,
  sourceCol: number,
): string => {
  if (!formula.trimStart().startsWith("=")) return formula;

  return transformUnquotedFormulaSegments(formula, (segment) =>
    convertUnquotedReferencesToR1C1(segment, sourceRow, sourceCol),
  );
};
